export const API_BASE_URL = process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');

export function getAuthToken() {
  let token = localStorage.getItem('authToken');
  if (!token) return null;
  token = token.toString().trim();
  if (token.startsWith('Bearer ')) token = token.slice('Bearer '.length).trim();
  if ((token.startsWith('"') && token.endsWith('"')) || (token.startsWith("'") && token.endsWith("'"))) {
    try { token = JSON.parse(token); } catch { token = token.slice(1, -1); }
  }
  return token;
}

// Designer co-edit context. When set by the designer-side "Open Garden App in
// co-edit mode" flow, every /api/garden/* request sends x-garden-as-user and
// x-garden-project-id headers. The server's authenticateGardenUser middleware
// validates these against garden_designer_access and rewrites req.user.id so
// all queries hit the homeowner's data. Clearing sessionStorage returns the
// app to the designer's own account.
const CO_EDIT_USER_KEY   = 'garden:designerCoEdit:userId';
const CO_EDIT_PROJECT_KEY = 'garden:designerCoEdit:projectId';
const CO_EDIT_LABEL_KEY  = 'garden:designerCoEdit:label';

export function setDesignerCoEdit({ userId, projectId, label } = {}) {
  if (typeof sessionStorage === 'undefined') return;
  if (!userId || !projectId) return;
  sessionStorage.setItem(CO_EDIT_USER_KEY, String(userId));
  sessionStorage.setItem(CO_EDIT_PROJECT_KEY, String(projectId));
  if (label) sessionStorage.setItem(CO_EDIT_LABEL_KEY, String(label));
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('garden:co-edit-changed'));
  }
}

export function clearDesignerCoEdit() {
  if (typeof sessionStorage === 'undefined') return;
  sessionStorage.removeItem(CO_EDIT_USER_KEY);
  sessionStorage.removeItem(CO_EDIT_PROJECT_KEY);
  sessionStorage.removeItem(CO_EDIT_LABEL_KEY);
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('garden:co-edit-changed'));
  }
}

export function getDesignerCoEdit() {
  if (typeof sessionStorage === 'undefined') return null;
  const userId = sessionStorage.getItem(CO_EDIT_USER_KEY);
  const projectId = sessionStorage.getItem(CO_EDIT_PROJECT_KEY);
  if (!userId || !projectId) return null;
  return { userId, projectId, label: sessionStorage.getItem(CO_EDIT_LABEL_KEY) || '' };
}

async function gardenFetch(path, options = {}) {
  const token = getAuthToken();
  const headers = { ...options.headers };
  const isForm = options.body instanceof FormData;
  if (!isForm) headers['Content-Type'] = headers['Content-Type'] || 'application/json';
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const coEdit = getDesignerCoEdit();
  if (coEdit) {
    headers['x-garden-as-user'] = coEdit.userId;
    headers['x-garden-project-id'] = coEdit.projectId;
  }

  const res = await fetch(`${API_BASE_URL}/garden${path}`, { ...options, headers });

  if (res.status === 401 || res.status === 403) {
    throw new Error('Authentication required');
  }
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
    // Preserve structured error info on the thrown Error so UI can react to
    // known error codes (e.g. rate_limited, not_found, too_generic) without
    // string-sniffing.
    const err = new Error(body.message || body.error || `HTTP ${res.status}`);
    err.status = res.status;
    err.code = body.error || null;
    err.retryAfter = body.retry_after_s || null;
    throw err;
  }
  return res.json();
}

export const SEASONS = ['spring', 'summer', 'fall', 'winter'];

export const zonesApi = {
  list: () => gardenFetch('/zones'),
  get: (id) => gardenFetch(`/zones/${id}`),
  create: (body) => gardenFetch('/zones', { method: 'POST', body: JSON.stringify(body) }),
  patch: (id, body) => gardenFetch(`/zones/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  remove: (id) => gardenFetch(`/zones/${id}`, { method: 'DELETE' }),
  uploadSeasonPhoto: (id, season, file) => {
    const fd = new FormData();
    fd.append('image', file);
    return gardenFetch(`/zones/${id}/photos/${season}`, { method: 'POST', body: fd });
  },
  removeSeasonPhoto: (id, season) =>
    gardenFetch(`/zones/${id}/photos/${season}`, { method: 'DELETE' }),
};

export const plantsApi = {
  list: () => gardenFetch('/plants'),
  get: (id) => gardenFetch(`/plants/${id}`),
  create: (body) => gardenFetch('/plants', { method: 'POST', body: JSON.stringify(body) }),
  patch: (id, body) => gardenFetch(`/plants/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  remove: (id) => gardenFetch(`/plants/${id}`, { method: 'DELETE' }),
};

export const markersApi = {
  list: (zoneId) => gardenFetch(`/zones/${zoneId}/markers`),
  create: (zoneId, body) =>
    gardenFetch(`/zones/${zoneId}/markers`, { method: 'POST', body: JSON.stringify(body) }),
  patch: (id, body) => gardenFetch(`/markers/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  remove: (id) => gardenFetch(`/markers/${id}`, { method: 'DELETE' }),
};

export const journalApi = {
  list: ({ plant_id, zone_id, from, to, limit } = {}) => {
    const qs = new URLSearchParams();
    if (plant_id) qs.set('plant_id', plant_id);
    if (zone_id)  qs.set('zone_id', zone_id);
    if (from)     qs.set('from', from);
    if (to)       qs.set('to', to);
    if (limit)    qs.set('limit', String(limit));
    const q = qs.toString();
    return gardenFetch(`/journal${q ? `?${q}` : ''}`);
  },
  get: (id) => gardenFetch(`/journal/${id}`),
  create: ({ body_text, plant_id, zone_id, entry_date, photos = [] } = {}) => {
    const fd = new FormData();
    if (body_text)  fd.append('body_text', body_text);
    if (plant_id)   fd.append('plant_id', plant_id);
    if (zone_id)    fd.append('zone_id', zone_id);
    if (entry_date) fd.append('entry_date', entry_date);
    for (const f of photos) fd.append('photos', f);
    return gardenFetch('/journal', { method: 'POST', body: fd });
  },
  patch: (id, body) =>
    gardenFetch(`/journal/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  addPhotos: (id, photos = []) => {
    const fd = new FormData();
    for (const f of photos) fd.append('photos', f);
    return gardenFetch(`/journal/${id}/photos`, { method: 'POST', body: fd });
  },
  removePhoto: (id, url) =>
    gardenFetch(`/journal/${id}/photo?url=${encodeURIComponent(url)}`, { method: 'DELETE' }),
  remove: (id) => gardenFetch(`/journal/${id}`, { method: 'DELETE' }),
};

export const tasksApi = {
  // window: 'this_month' | 'next_3m' | 'year'
  list: (window = 'next_3m') => gardenFetch(`/tasks?window=${encodeURIComponent(window)}`),
  complete: (taskKey) =>
    gardenFetch(`/tasks/${encodeURIComponent(taskKey)}/complete`, { method: 'PUT' }),
  uncomplete: (taskKey) =>
    gardenFetch(`/tasks/${encodeURIComponent(taskKey)}/complete`, { method: 'DELETE' }),

  // User-authored tasks. Body shape:
  //   { label, notes?, due_year, due_month, garden_plant_id? }
  createCustom: (body) =>
    gardenFetch('/tasks/custom', { method: 'POST', body: JSON.stringify(body) }),
  updateCustom: (id, body) =>
    gardenFetch(`/tasks/custom/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  removeCustom: (id) =>
    gardenFetch(`/tasks/custom/${encodeURIComponent(id)}`, { method: 'DELETE' }),
};

export const aiApi = {
  plantLookup: ({ common_name, botanical_name, section, hardiness_zone } = {}) =>
    gardenFetch('/ai/plant-lookup', {
      method: 'POST',
      body: JSON.stringify({
        common_name,
        botanical_name,
        section,
        ...(hardiness_zone != null && String(hardiness_zone).trim() !== ''
          ? { hardiness_zone: String(hardiness_zone).trim() }
          : {}),
      }),
    }),
  // Chat: message + optional single image. History is loaded server-side from
  // garden_chat_messages, so clients don't resend prior turns.
  chat: ({ message, image, plant_id, zone_id, conversation_id } = {}) => {
    const fd = new FormData();
    if (message)         fd.append('message', message);
    if (image)           fd.append('image', image);
    if (plant_id)        fd.append('plant_id', plant_id);
    if (zone_id)         fd.append('zone_id', zone_id);
    if (conversation_id) fd.append('conversation_id', conversation_id);
    return gardenFetch('/ai/chat', { method: 'POST', body: fd });
  },
  chatHistory: (conversation_id) => {
    const q = conversation_id ? `?conversation_id=${encodeURIComponent(conversation_id)}` : '';
    return gardenFetch(`/ai/chat/history${q}`);
  },
  clearChatHistory: (conversation_id) => {
    const q = conversation_id ? `?conversation_id=${encodeURIComponent(conversation_id)}` : '';
    return gardenFetch(`/ai/chat/history${q}`, { method: 'DELETE' });
  },
};

export const settingsApi = {
  get: () => gardenFetch('/settings'),
  put: (body) => gardenFetch('/settings', { method: 'PUT', body: JSON.stringify(body) }),
};

// Homeowner-side management of designer co-edit access. Mirrors
// server/routes/garden/designer-access.js. Toggle `enabled` to turn a
// designer's co-edit ability on or off; `remove` deletes the access record
// entirely (the designer is fully removed until a fresh handoff invite is
// accepted).
export const designerAccessApi = {
  list: () => gardenFetch('/designer-access'),
  setEnabled: (id, enabled) =>
    gardenFetch(`/designer-access/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify({ enabled: !!enabled }),
    }),
  remove: (id) =>
    gardenFetch(`/designer-access/${encodeURIComponent(id)}`, { method: 'DELETE' }),
};

export const scanApi = {
  // Send a plant-tag photo to the server for OCR + AI parsing.
  // Returns: { image_url, ocr_text, plant, confidence, ai_available }.
  // The server returns 200 even on AI failure with empty `plant` / `ocr_text`,
  // so callers can always proceed to the confirmation screen.
  tag: (file) => {
    const fd = new FormData();
    fd.append('image', file);
    return gardenFetch('/scan/tag', { method: 'POST', body: fd });
  },
};

export const photosApi = {
  list: (plantId) => gardenFetch(`/plants/${plantId}/photos`),
  upload: (plantId, file, { season, caption, taken_at } = {}) => {
    const fd = new FormData();
    fd.append('image', file);
    if (season) fd.append('season', season);
    if (caption) fd.append('caption', caption);
    if (taken_at) fd.append('taken_at', taken_at);
    return gardenFetch(`/plants/${plantId}/photos`, { method: 'POST', body: fd });
  },
  patch: (id, body) => gardenFetch(`/photos/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  remove: (id) => gardenFetch(`/photos/${id}`, { method: 'DELETE' }),
};

// Small helper: pick the best photo URL for a zone given a preferred season.
// Falls back to: preferred -> default_season -> any non-null season -> null.
export function pickZonePhoto(zone, preferredSeason) {
  if (!zone) return { url: null, season: null };
  const order = [preferredSeason, zone.default_season, 'spring', 'summer', 'fall', 'winter']
    .filter(Boolean);
  for (const s of order) {
    const url = zone[`photo_${s}_url`];
    if (url) return { url, season: s };
  }
  return { url: null, season: null };
}
